import React, { useCallback, useEffect, useMemo, useState } from "react";
import { AlertTriangle, CheckCircle2, Link2, Link2Off, RefreshCw, Undo2, Wrench, Info, GitMerge } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";
import { Label } from "./ui/label";
import { Skeleton } from "./ui/skeleton";
import { Switch } from "./ui/switch";
import { api, errMsg } from "../lib/api";

const n = (v) => Number(v || 0).toLocaleString("en-IN");

/**
 * Phase 160 — Area linkage check + repair.
 * Finds properties whose zone / ward / mohalla ids do not line up with Master Data (a ward of another zone, a
 * mohalla of another ward, deactivated or missing masters) — the reason a ward or mohalla filter shows 0 while
 * the export prints the right-looking name — and relinks them in bulk. Every repair is a run that can be undone;
 * collector assignments re-sync for every ward touched.
 */
export function AreaLinkageDialog({ code, open, onOpenChange, onDone }) {
  const [scan, setScan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [createMissing, setCreateMissing] = useState(true);
  const [picked, setPicked] = useState(() => new Set());
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [runs, setRuns] = useState([]);

  const loadRuns = useCallback(() => {
    api.get(`/t/${code}/area-linkage/runs`, { params: { limit: 5 } }).then((r) => setRuns(r.data || [])).catch(() => {});
  }, [code]);

  const runScan = useCallback(async (create) => {
    setLoading(true); setError(null); setResult(null); setConfirming(false);
    try {
      const r = await api.get(`/t/${code}/area-linkage/scan`, { params: { create_missing_mohallas: create } });
      setScan(r.data);
      setPicked(new Set((r.data.issues || []).filter((i) => i.fixable).map((i) => i.key)));
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, [code]);

  useEffect(() => {
    if (!open) { setScan(null); setResult(null); setError(null); setConfirming(false); return; }
    runScan(createMissing);
    loadRuns();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const toggleCreate = (v) => { setCreateMissing(v); runScan(v); };

  const issues = scan?.issues || [];
  const fixable = issues.filter((i) => i.fixable);
  const pickedIssues = fixable.filter((i) => picked.has(i.key));
  const pickedCount = pickedIssues.reduce((a, i) => a + i.count, 0);
  const allPicked = fixable.length > 0 && fixable.every((i) => picked.has(i.key));
  const wardsTouched = useMemo(() => {
    const s = new Set();
    pickedIssues.forEach((i) => { if (i.current.ward_name) s.add(i.current.ward_name); if (i.fix_labels?.ward_id) s.add(i.fix_labels.ward_id); });
    return [...s];
  }, [pickedIssues]);

  const repair = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/t/${code}/area-linkage/repair`, { keys: allPicked ? null : [...picked], create_missing_mohallas: createMissing, confirm: true });
      setResult(r.data);
      setConfirming(false);
      toast.success(`${n(r.data.fixed)} propert${r.data.fixed === 1 ? "y" : "ies"} re-linked`, {
        description: r.data.assignment_sync === "started" ? `Collector assignments are re-syncing across ${r.data.wards.length} ward${r.data.wards.length === 1 ? "" : "s"}.` : undefined,
      });
      onDone?.(r.data);
      loadRuns();
      const s = await api.get(`/t/${code}/area-linkage/scan`, { params: { create_missing_mohallas: createMissing } });
      setScan(s.data);
      setPicked(new Set((s.data.issues || []).filter((i) => i.fixable).map((i) => i.key)));
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const undo = async (runId) => {
    setBusy(true);
    try {
      const r = await api.post(`/t/${code}/area-linkage/runs/${runId}/undo`);
      toast.success(`${n(r.data.reverted)} propert${r.data.reverted === 1 ? "y" : "ies"} restored`);
      setResult(null);
      onDone?.(r.data);
      await runScan(createMissing);
      loadRuns();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const summary = scan?.summary || {};
  const clean = scan && summary.issues === 0;

  return (
    <Dialog open={open} onOpenChange={(v) => !busy && onOpenChange(v)}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="area-linkage-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2"><Link2 className="h-5 w-5 text-primary" /> Check area links</DialogTitle>
          <DialogDescription data-testid="area-linkage-description">
            Every property should point at a ward inside its zone and a mohalla inside its ward. When an import linked
            a same-named ward of another zone or a mohalla of another ward, the Properties filters and the collector's
            territory show 0 while the export still prints the name. This check finds those properties and re-links them.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border bg-muted/30 px-3 py-2">
          <div className="flex items-center gap-2">
            <Switch id="area-linkage-create" checked={createMissing} onCheckedChange={toggleCreate} disabled={loading || busy} data-testid="area-linkage-create-switch" />
            <Label htmlFor="area-linkage-create" className="text-sm leading-tight">
              Create missing mohallas under the property's ward
              <span className="block text-[11px] text-muted-foreground font-normal">Off = the stray mohalla is cleared and the property stays with its ward only</span>
            </Label>
          </div>
          <Button variant="outline" size="sm" onClick={() => runScan(createMissing)} disabled={loading || busy} data-testid="area-linkage-rescan">
            <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} /> Re-check
          </Button>
        </div>

        {error ? (
          <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive" data-testid="area-linkage-error">
            {error} <Button variant="link" size="sm" className="h-auto p-0 ml-1" onClick={() => runScan(createMissing)}>Retry</Button>
          </div>
        ) : loading || !scan ? (
          <div className="space-y-2" data-testid="area-linkage-loading"><Skeleton className="h-14 w-full" /><Skeleton className="h-24 w-full" /><Skeleton className="h-8 w-1/2" /></div>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="area-linkage-summary">
              <Stat label="Properties checked" value={n(scan.checked)} testId="area-linkage-checked" />
              <Stat label="Mis-linked" value={n(summary.properties_affected)} tone={summary.properties_affected ? "warn" : "ok"} testId="area-linkage-affected" />
              <Stat label="Fixable now" value={n(summary.fixable)} tone={summary.fixable ? "primary" : undefined} testId="area-linkage-fixable" />
              <Stat label="Needs Master Data fix" value={n(summary.unfixable)} tone={summary.unfixable ? "warn" : undefined} testId="area-linkage-unfixable" />
            </div>

            {result && (
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm flex items-start gap-2" data-testid="area-linkage-result">
                <CheckCircle2 className="h-4 w-4 text-emerald-700 mt-0.5 shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-emerald-900">{n(result.fixed)} propert{result.fixed === 1 ? "y" : "ies"} re-linked in {result.groups} group{result.groups === 1 ? "" : "s"}
                    {result.created_mohallas?.length ? ` · ${result.created_mohallas.length} mohalla${result.created_mohallas.length === 1 ? "" : "s"} created` : ""}</p>
                  <p className="text-emerald-800/80 text-xs mt-0.5">
                    Collector assignments are re-syncing for {result.wards.length} ward{result.wards.length === 1 ? "" : "s"}. Re-open the Properties page filter to see the rows.
                  </p>
                </div>
                <Button variant="outline" size="sm" className="h-7 text-xs shrink-0" onClick={() => undo(result.run_id)} disabled={busy} data-testid="area-linkage-undo-last">
                  <Undo2 className="h-3.5 w-3.5 mr-1" /> Undo
                </Button>
              </div>
            )}

            {clean ? (
              <div className="rounded-lg border border-dashed p-6 text-center" data-testid="area-linkage-clean">
                <CheckCircle2 className="h-8 w-8 mx-auto text-emerald-600 mb-2" />
                <p className="font-medium">All area links are consistent</p>
                <p className="text-sm text-muted-foreground mt-1">Every live property points at a ward inside its zone and a mohalla inside its ward.</p>
              </div>
            ) : (
              <>
                {scan.by_ward?.length > 0 && (
                  <section className="space-y-1.5">
                    <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">By ward</h4>
                    <div className="flex flex-wrap gap-1.5" data-testid="area-linkage-by-ward">
                      {scan.by_ward.slice(0, 12).map((w) => (
                        <Badge key={`${w.ward_name}|${w.zone_name}`} variant="outline" className="font-normal text-xs" data-testid={`area-linkage-ward-${w.ward_name}`}>
                          <span className="font-medium">{w.ward_name}</span>{w.zone_name ? <span className="text-muted-foreground">&nbsp;· {w.zone_name}</span> : null}
                          <span className="ml-1.5 tabular-nums">{n(w.count)}</span>
                        </Badge>
                      ))}
                      {scan.by_ward.length > 12 && <span className="text-xs text-muted-foreground self-center">+{scan.by_ward.length - 12} more</span>}
                    </div>
                  </section>
                )}

                <section className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1.5">
                      <Link2Off className="h-3.5 w-3.5" /> Mis-linked groups ({issues.length})
                    </h4>
                    {fixable.length > 0 && (
                      <button type="button" className="text-xs text-primary hover:underline"
                        onClick={() => setPicked(allPicked ? new Set() : new Set(fixable.map((i) => i.key)))} data-testid="area-linkage-toggle-all">
                        {allPicked ? "Untick all" : "Tick all fixable"}
                      </button>
                    )}
                  </div>
                  <ul className="rounded-lg border divide-y max-h-[38vh] overflow-y-auto overscroll-contain" data-testid="area-linkage-issues">
                    {issues.map((i) => (
                      <li key={i.key} className={`flex items-start gap-3 p-2.5 ${i.fixable ? "" : "bg-muted/30"}`} data-testid={`area-linkage-issue-${i.key}`} data-fixable={i.fixable ? "true" : "false"}>
                        <div className="pt-0.5">
                          {i.fixable ? (
                            <Checkbox checked={picked.has(i.key)} disabled={busy}
                              onCheckedChange={(on) => setPicked((prev) => { const s = new Set(prev); if (on) s.add(i.key); else s.delete(i.key); return s; })}
                              aria-label={`Repair ${i.current.ward_name}`} data-testid={`area-linkage-pick-${i.key}`} />
                          ) : <AlertTriangle className="h-4 w-4 text-amber-600" />}
                        </div>
                        <div className="min-w-0 flex-1 text-sm">
                          <p className="leading-tight">
                            <span className="font-medium tabular-nums">{n(i.count)}</span> propert{i.count === 1 ? "y" : "ies"} ·{" "}
                            <span className="font-medium">{i.current.ward_name || "unknown ward"}</span>
                            {i.current.zone_name && <span className="text-muted-foreground"> (zone {i.current.zone_name})</span>}
                            {i.current.mohalla_name && <> · mohalla <span className="font-medium">{i.current.mohalla_name}</span>
                              {i.current.mohalla_ward_name && i.current.mohalla_ward_name !== i.current.ward_name && <span className="text-muted-foreground"> of {i.current.mohalla_ward_name}</span>}</>}
                          </p>
                          <p className="text-xs text-amber-800 mt-0.5">{i.labels.join(" · ")}</p>
                          <p className="text-xs text-muted-foreground mt-0.5" data-testid={`area-linkage-fix-${i.key}`}>
                            {i.fixable ? <><Wrench className="inline h-3 w-3 mr-1 align-[-2px]" />{i.notes.join("; ")}</> : <><Info className="inline h-3 w-3 mr-1 align-[-2px]" />{i.notes.join("; ") || "Needs Master Data fix first"}</>}
                          </p>
                          {i.sample_pins?.length > 0 && <p className="text-[11px] text-muted-foreground/80 mt-0.5 font-mono truncate">e.g. {i.sample_pins.join(", ")}</p>}
                        </div>
                      </li>
                    ))}
                  </ul>
                </section>
              </>
            )}

            {scan.duplicates?.length > 0 && (
              <section className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm" data-testid="area-linkage-duplicates">
                <p className="font-medium text-amber-900 flex items-center gap-1.5"><GitMerge className="h-4 w-4" /> Same-named masters inside one parent</p>
                <p className="text-xs text-amber-800/80 mt-0.5">These are never auto-fixed — merge them under Master Data › Merge duplicates, then re-check.</p>
                <ul className="mt-1.5 flex flex-wrap gap-1.5">
                  {scan.duplicates.slice(0, 10).map((d, idx) => (
                    <Badge key={idx} variant="outline" className="font-normal text-xs bg-background">{d.level}: {d.name} <span className="text-muted-foreground">&nbsp;in {d.parent}</span> ×{d.ids.length}</Badge>
                  ))}
                </ul>
              </section>
            )}

            {runs.filter((r) => !r.undone_at).length > 0 && !result && (
              <section className="space-y-1">
                <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Recent repairs</h4>
                <ul className="rounded-lg border divide-y text-xs" data-testid="area-linkage-runs">
                  {runs.filter((r) => !r.undone_at).slice(0, 3).map((r) => (
                    <li key={r.id} className="flex items-center gap-2 p-2">
                      <span className="flex-1 min-w-0 truncate">{n(r.fixed)} re-linked in {r.groups} group{r.groups === 1 ? "" : "s"} · {new Date(r.created_at).toLocaleString("en-IN")} · {r.created_by_name}</span>
                      <Button variant="ghost" size="sm" className="h-6 px-2 text-xs" onClick={() => undo(r.id)} disabled={busy} data-testid={`area-linkage-undo-${r.id}`}>
                        <Undo2 className="h-3 w-3 mr-1" /> Undo
                      </Button>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {confirming && (
              <div className="rounded-lg border border-primary/30 bg-primary/5 p-3 text-sm" data-testid="area-linkage-confirm">
                <p className="font-medium">Re-link {n(pickedCount)} propert{pickedCount === 1 ? "y" : "ies"} in {pickedIssues.length} group{pickedIssues.length === 1 ? "" : "s"}?</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Wards touched: {wardsTouched.slice(0, 8).join(", ")}{wardsTouched.length > 8 ? ` +${wardsTouched.length - 8} more` : ""}. Collector assignments re-sync afterwards. You can undo this repair.
                </p>
              </div>
            )}
          </div>
        )}

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy} data-testid="area-linkage-close">Close</Button>
          {!clean && scan && !error && (confirming ? (
            <Button onClick={repair} disabled={busy || pickedCount === 0} data-testid="area-linkage-repair-confirm">
              <Wrench className="h-4 w-4 mr-2" /> {busy ? "Re-linking…" : `Yes, re-link ${n(pickedCount)}`}
            </Button>
          ) : (
            <Button onClick={() => setConfirming(true)} disabled={busy || loading || pickedCount === 0} data-testid="area-linkage-repair">
              <Wrench className="h-4 w-4 mr-2" /> Repair {pickedCount ? n(pickedCount) : ""} selected
            </Button>
          ))}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const Stat = ({ label, value, tone, testId }) => (
  <div className={`rounded-lg border p-2.5 ${tone === "warn" ? "bg-amber-50 border-amber-200" : tone === "ok" ? "bg-emerald-50 border-emerald-200" : tone === "primary" ? "bg-primary/5 border-primary/20" : "bg-muted/40"}`}>
    <p className="text-[11px] text-muted-foreground">{label}</p>
    <p className={`text-lg font-semibold tabular-nums leading-tight ${tone === "warn" ? "text-amber-800" : tone === "ok" ? "text-emerald-800" : tone === "primary" ? "text-primary" : ""}`} data-testid={testId}>{value}</p>
  </div>
);
